import React, { FC, useContext } from 'react';
import { Box, Link, Tooltip, Typography, useTheme } from '@mui/material';
import StarRating from '../StarRating/StarRating';
import { InfoOutlined, OpenInNew } from '@mui/icons-material';
import { constants } from '../../constants/constants';
import { useTranslation } from 'react-i18next';
import Helper from '../../shared/helpers/Helper';
import Note from '../Notes/Note';
import { AuthContext } from '../../providers/AuthProvider';
import DeleteReview from './DeleteReview';
import PlatformService from '../../services/PlatformService';

interface Props {
  rating: number;
  platform: string;
  reviewFor?: string | null;
  timestamp?: string | null;
  reviewLink?: string | null;
  reviewStatus?: string | null;
  originalRating?: number | null;
  id: string;
  note: string | undefined;
  deletion: boolean;
  commentingModule: boolean;
  deleted: boolean;
  hide: boolean;
}

const ReviewHeader: FC<Props> = ({
  rating,
  platform,
  timestamp,
  reviewFor,
  reviewLink,
  originalRating,
  id,
  note,
  deletion,
  commentingModule,
  deleted,
  hide,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { hasRole } = useContext(AuthContext);

  if (hide) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mt: theme.spacing(1) }}>
      <StarRating
        value={rating}
        sx={{ pl: theme.spacing(2) }}
      />
      <Box sx={{ display: 'flex', ml: theme.spacing(1), cursor: 'pointer' }}>
        <Tooltip
          title={`${t('info_rating')}: ${originalRating}`}
          placement="top"
          arrow
        >
          <InfoOutlined
            fontSize="small"
            sx={{ color: theme.palette.primary.main }}
          />
        </Tooltip>
      </Box>
      <img
        src={constants.platform_logos[platform]}
        alt={`${platform} Logo`}
        style={{
          maxHeight: '32px',
          maxWidth: '76px',
          marginLeft: theme.spacing(1),
        }}
      />
      {reviewFor && (
        <Tooltip
          title={`${t('reviewed_doctor')}:`}
          placement="top"
          arrow
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              cursor: 'pointer',
              ml: theme.spacing(1),
            }}
          >
            {reviewFor}
          </Typography>
        </Tooltip>
      )}
      {(timestamp || reviewLink) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            ml: 'auto',
            pr: 1,
          }}
        >
          {deleted && hasRole('can-use-deleted-reviews') && (
            <Box marginRight={theme.spacing(4)}>
              <Tooltip
                title={
                  PlatformService.deletedCanBeOutdated(platform)
                    ? t('review_deleted_or_outdated')
                    : t('review_deleted')
                }
                arrow
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.error.main,
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 700,
                    fontFamily: 'Roboto, sans-serif',
                  }}
                  aria-disabled
                >
                  {t('deleted')}
                </Typography>
              </Tooltip>
            </Box>
          )}
          {hasRole('can-use-note') && (
            <Note
              note={note}
              id={id}
              type="comment"
            />
          )}
          {timestamp && (
            <Tooltip
              title={Helper.getFormattedDate(timestamp, false)}
              arrow
            >
              <Typography
                variant="body2"
                sx={{ cursor: 'pointer' }}
              >
                {Helper.getFormattedDate(timestamp, true)}
              </Typography>
            </Tooltip>
          )}
          {commentingModule && hasRole('can-use-deletion') && (
            <DeleteReview
              id={id}
              deletion={deletion}
            />
          )}
          {reviewLink && (
            <Tooltip
              title={t('to_review')}
              arrow
            >
              <Link
                href={reviewLink}
                underline="none"
                target="_blank"
                rel="noreferrer"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <OpenInNew />
              </Link>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ReviewHeader;
